import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
// Icons – only import what we actually use to keep bundle size small
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BarChartIcon from '@mui/icons-material/BarChart';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import WarriliLogo from '../assets/logo-white.png';

/* -------------------------------------------------------------------------- */
/*                                Styled API                                 */
/* -------------------------------------------------------------------------- */

const WorksContainer = styled(Box)({
  backgroundColor: '#000000',
  color: '#ffffff',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
});

const Section = styled(Box)(({ theme, bgcolor = '#000000', color = '#ffffff' }) => ({
  backgroundColor: bgcolor,
  color,
  padding: '80px 0',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '50px',
  fontWeight: 700,
  textAlign: 'center',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-15px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    backgroundColor: '#FFD700',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const InfoCard = styled(Card)({
  position: 'relative',
  height: '100%',
  backgroundColor: '#111111',
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)',
  },
});

const IconContainer = styled(Box)({
  backgroundColor: '#FFD700',
  color: '#000000',
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px auto',
  '& svg': {
    fontSize: '2rem',
  },
});

const StepIconContainer = styled(Box)({
  backgroundColor: '#2196f3',
  color: '#ffffff',
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px auto',
  '& svg': {
    fontSize: '2rem',
  },
});

const CTAButton = styled(Button)({
  backgroundColor: '#FFD700',
  color: '#000000',
  padding: '12px 30px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '50px',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: '#e6c200',
  },
});

const BetaBanner = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFD700',
  color: '#000000',
  textAlign: 'center',
  padding: theme.spacing(1),
  fontWeight: 600,
  fontSize: '0.9rem',
}));

const StepNumber = styled(Box)({
  position: 'absolute',
  top: 16,
  left: 16,
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: '#FFD700',
  color: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '20px',
  backgroundColor: '#000',
});

const LogoImage = styled('img')({
  height: '40px',
});

/* -------------------------------------------------------------------------- */
/*                               Static Data                                 */
/* -------------------------------------------------------------------------- */

const steps = [
  {
    icon: <SmartToyIcon />,
    title: 'Describe Your Issue',
    desc: 'Tell Warrili what problem you are facing in natural language or through quick selections. Our AI will guide you through a series of questions to understand the issue better.',
  },
  {
    icon: <SpeedIcon />,
    title: 'Real-Time AI Diagnosis',
    desc: 'Our proprietary AI instantly analyses your input and identifies the root cause. This step helps us narrow down the possible solutions and provide you with the most accurate results.',
  },
  {
    icon: <SupportAgentIcon />,
    title: 'Connect With Experts',
    desc: 'If the issue needs a human touch, seamlessly schedule a video session with a vetted expert. Our experts are available 24/7 to help you resolve your issue.',
  },
  {
    icon: <BuildIcon />,
    title: 'Guided Resolution',
    desc: 'Follow step-by-step instructions or receive live assistance until the problem is fixed. Our guided resolution process ensures that you get the help you need to resolve your issue.',
  },
  {
    icon: <CheckCircleIcon />,
    title: 'Confirm & Rate',
    desc: 'Close the loop by rating the service so our models continuously improve. Your feedback helps us refine our AI and provide better support in the future.',
  },
];

const revenues = [
  {
    icon: <AttachMoneyIcon />,
    title: 'Subscription Plans',
    desc: 'Recurring revenue from tiered personal & business plans with premium AI features.',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'Pay-Per-Consultation',
    desc: 'Commission on each expert video consultation booked through the platform.',
  },
  {
    icon: <StorefrontIcon />,
    title: 'Integrated Marketplace',
    desc: 'Affiliate earnings from hardware, software & spare-part sales recommended in sessions.',
  },
  {
    icon: <BarChartIcon />,
    title: 'Data Insights',
    desc: 'Anonymous trend analytics for OEMs & partners creates an additional SaaS revenue stream.',
  },
];

/* -------------------------------------------------------------------------- */
/*                               Entry Point                                 */
/* -------------------------------------------------------------------------- */

const Works = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <WorksContainer>
      {/* Logo Header */}
      <Header>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <LogoImage src={WarriliLogo} alt="Warrili Logo" />
        </Link>
      </Header>
      {/* Beta Notification */}
      <BetaBanner>
        Beta Notice: Warrili is in experimental beta – interfaces & features may change.
      </BetaBanner>
      {/* HOW IT WORKS SECTION */}
      <Section>
        <Container maxWidth="lg">
          <SectionTitle variant="h2">How It Works</SectionTitle>
          <Grid container spacing={4}>
            {steps.map((step, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <InfoCard elevation={3}>
                  <StepNumber>{idx + 1}</StepNumber>
                  <CardContent sx={{ p: 4, pt: 6 }}>
                    <StepIconContainer>{step.icon}</StepIconContainer>
                    <Typography variant="h5" align="center" fontWeight={600} gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" align="center" color="#e0e0e0">
                      {step.desc}
                    </Typography>
                  </CardContent>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* REVENUE / BENEFITS SECTION */}
      <Section bgcolor="#FFD700" color="#000000">
        <Container maxWidth="lg">
          <SectionTitle variant="h2" sx={{ '&:after': { backgroundColor: '#000000' } }}>
            Value & Revenue Model
          </SectionTitle>
          <Grid container spacing={4}>
            {revenues.map((rev, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <InfoCard elevation={0} sx={{ backgroundColor: '#ffffff', color: '#000000' }}>
                  <CardContent sx={{ p: 4 }}>
                    <IconContainer sx={{ backgroundColor: '#000000', color: '#FFD700' }}>
                      {rev.icon}
                    </IconContainer>
                    <Typography variant="h6" align="center" fontWeight={700} gutterBottom>
                      {rev.title}
                    </Typography>
                    <Typography variant="body2" align="center" color="#555">
                      {rev.desc}
                    </Typography>
                  </CardContent>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* CALL TO ACTION */}
      <Section>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={700} gutterBottom>
            Ready to super-charge your tech support?
          </Typography>
          <CTAButton
            variant="contained"
            size="large"
            component={Link}
            to="/signup"
            startIcon={<RocketLaunchIcon />}
          >
            Get Started
          </CTAButton>
        </Container>
      </Section>
    </WorksContainer>
  );
};

export default Works;